// Loss factor rows each carry TWO financial years: current_fy/current_* (the
// year the workbook was published for) and prior_fy/prior_* (the year before).
// Resolving a factor therefore has to pick a year, and the right year is the one
// the BILL falls in — revalidating a 2025-26 invoice with 2026-27 factors would
// report a variance that never existed.
//
// This is the only place that rule lives, so the site form, the site detail
// refresh and the lookup panel cannot disagree about which year applies.

/** A row shape common to both transmission and distribution loss factor rows. */
export type FyRow = {
  current_fy: string;
  prior_fy?: string | null;
};

export type PickedFactor = {
  /** The chosen value, or null when neither year carries one. */
  value: number | null;
  /** The financial year the value came from, e.g. '2026-27'. */
  fy: string | null;
  /** Which column supplied it — useful for explaining a fallback to the user. */
  source: 'current' | 'prior' | null;
  /** True when the bill's own year had no value and the other year was used. */
  fellBack: boolean;
};

/**
 * Australian financial year for an ISO date: 1 July – 30 June.
 * '2026-07-01' → '2026-27';  '2026-06-30' → '2025-26'.
 * Returns null for anything that is not a usable YYYY-MM-DD date.
 */
export const financialYearOf = (isoDate: string | null | undefined): string | null => {
  const s = (isoDate ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // July onwards belongs to the FY starting that calendar year.
  const startYear = month >= 7 ? year : year - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
};

/**
 * Today as YYYY-MM-DD in LOCAL time. `toISOString()` is UTC, which in Australia
 * reports the previous day for most of the evening — on 30 June / 1 July that
 * would pick the wrong financial year.
 */
export const todayIso = (d: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Pick the loss factor value that applies to `targetFy`.
 *
 * Prefers the column whose financial year matches. When the matching column has
 * no value — the row predates the bill, or a carried-forward row has no prior —
 * falls back to the other year rather than returning nothing, because a factor
 * from the adjacent year is far closer to correct than silently applying 1.0.
 *
 * `targetFy` of null (unknown bill period) means "use the latest year on the
 * row", which is the current column.
 */
export const pickFactorForFy = <Row extends FyRow>(
  row: Row | null | undefined,
  targetFy: string | null,
  currentKey: keyof Row,
  priorKey: keyof Row,
): PickedFactor => {
  if (!row) return { value: null, fy: null, source: null, fellBack: false };

  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const current = num(row[currentKey]);
  const prior = num(row[priorKey]);
  const priorFy = (row.prior_fy ?? '') || null;

  // The bill's year matches the prior column — use it, and only fall back to
  // current when the prior column is empty.
  if (targetFy && priorFy === targetFy) {
    if (prior !== null) return { value: prior, fy: priorFy, source: 'prior', fellBack: false };
    if (current !== null) return { value: current, fy: row.current_fy, source: 'current', fellBack: true };
    return { value: null, fy: null, source: null, fellBack: false };
  }

  // Otherwise prefer current (the matching case, and the unknown-period case).
  if (current !== null) {
    return { value: current, fy: row.current_fy, source: 'current', fellBack: Boolean(targetFy) && row.current_fy !== targetFy };
  }
  if (prior !== null) {
    return { value: prior, fy: priorFy, source: 'prior', fellBack: true };
  }
  return { value: null, fy: null, source: null, fellBack: false };
};

/** Convenience wrapper for a transmission row. */
export const pickMlfForDate = <Row extends FyRow>(row: Row | null | undefined, billFrom: string | null | undefined): PickedFactor =>
  pickFactorForFy(row, financialYearOf(billFrom), 'current_mlf' as keyof Row, 'prior_mlf' as keyof Row);

/** Convenience wrapper for a distribution row. */
export const pickDlfForDate = <Row extends FyRow>(row: Row | null | undefined, billFrom: string | null | undefined): PickedFactor =>
  pickFactorForFy(row, financialYearOf(billFrom), 'current_dlf' as keyof Row, 'prior_dlf' as keyof Row);

/** Inclusive day count between two ISO dates, or null if either is unusable. */
const daysBetween = (fromIso: string, toIso: string): number | null => {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.round((b - a) / 86_400_000) + 1;
};

/** 1 July of the financial year starting in `startYear`, as an ISO date. */
const fyStart = (fy: string): string => `${fy.slice(0, 4)}-07-01`;

/**
 * The financial years a billing period covers, with the number of days in each.
 *
 * A period wholly inside one year yields a single segment. One that straddles
 * 1 July yields two — 8 of production's 1647 bills do, and AEMO reissues every
 * factor on that date, so pricing the whole period at one year's factor is
 * wrong by the size of the year-on-year move (Corowa Solar moved 4.6%).
 */
export const financialYearSegments = (
  billFrom: string | null | undefined,
  billTo: string | null | undefined,
): Array<{ fy: string; days: number }> => {
  const from = (billFrom ?? '').trim();
  const to = (billTo ?? '').trim();
  const startFy = financialYearOf(from);
  const endFy = financialYearOf(to);
  if (!startFy) return [];
  if (!endFy || startFy === endFy) {
    const days = daysBetween(from, to) ?? 1;
    return [{ fy: startFy, days: Math.max(days, 1) }];
  }

  // Walk 1 July boundaries so a period spanning more than one still adds up.
  const segments: Array<{ fy: string; days: number }> = [];
  let cursor = from;
  let fy = startFy;
  while (fy !== endFy) {
    const nextStart = fyStart(`${Number(fy.slice(0, 4)) + 1}`);
    const lastDay = new Date(Date.parse(`${nextStart}T00:00:00Z`) - 86_400_000)
      .toISOString().slice(0, 10);
    const days = daysBetween(cursor, lastDay);
    if (days === null) break;
    segments.push({ fy, days });
    cursor = nextStart;
    fy = financialYearOf(cursor) ?? endFy;
  }
  const tail = daysBetween(cursor, to);
  if (tail !== null && tail > 0) segments.push({ fy: endFy, days: tail });
  return segments.filter((s) => s.days > 0);
};

/**
 * The factor to apply across a whole billing PERIOD, not a single date.
 *
 * Within one financial year this is just pickFactorForFy. Across a 1 July
 * boundary it is the day-weighted average of each year's factor, which is the
 * exact answer when consumption is even across the period and a far closer one
 * than either year alone when it is not.
 *
 * `fy` names every year that contributed, and `blended` says whether more than
 * one did, so the caller can show why the number is not either published value.
 */
export const pickFactorForPeriod = <Row extends FyRow>(
  row: Row | null | undefined,
  billFrom: string | null | undefined,
  billTo: string | null | undefined,
  currentKey: keyof Row,
  priorKey: keyof Row,
): PickedFactor & { blended: boolean } => {
  const segments = financialYearSegments(billFrom, billTo);
  if (!row || segments.length === 0) {
    const single = pickFactorForFy(row, financialYearOf(billFrom), currentKey, priorKey);
    return { ...single, blended: false };
  }
  if (segments.length === 1) {
    const single = pickFactorForFy(row, segments[0].fy, currentKey, priorKey);
    return { ...single, blended: false };
  }

  const parts = segments
    .map((s) => ({ ...s, picked: pickFactorForFy(row, s.fy, currentKey, priorKey) }))
    .filter((p) => p.picked.value !== null);
  const totalDays = parts.reduce((sum, p) => sum + p.days, 0);
  if (totalDays === 0) return { value: null, fy: null, source: null, fellBack: false, blended: false };

  const value = parts.reduce((sum, p) => sum + (p.picked.value as number) * p.days, 0) / totalDays;
  const usedFys = [...new Set(parts.map((p) => p.picked.fy).filter(Boolean))] as string[];
  return {
    value,
    fy: usedFys.join(' + '),
    // 'current' only when every segment resolved from the current column.
    source: parts.every((p) => p.picked.source === 'current') ? 'current'
      : parts.every((p) => p.picked.source === 'prior') ? 'prior' : 'current',
    fellBack: parts.some((p) => p.picked.fellBack),
    blended: usedFys.length > 1,
  };
};

/** Period-aware wrappers, the ones callers should reach for. */
export const mlfForPeriod = <Row extends FyRow>(row: Row | null | undefined, from: string | null | undefined, to: string | null | undefined) =>
  pickFactorForPeriod(row, from, to, 'current_mlf' as keyof Row, 'prior_mlf' as keyof Row);

export const dlfForPeriod = <Row extends FyRow>(row: Row | null | undefined, from: string | null | undefined, to: string | null | undefined) =>
  pickFactorForPeriod(row, from, to, 'current_dlf' as keyof Row, 'prior_dlf' as keyof Row);
